import debounce from 'lodash/debounce';
import React, { useState, useMemo, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useUnmounted from '../../hooks/useUnmounted';
import { companySearch } from './service';
import Combobox from '../combobox/Combobox';
import Input from '../input/Input';
import Button from '../button/Button';
import * as classes from './SmeLendingForm.module.scss';

const createValidator = () => {
  return yup.object().shape({
    businessName: yup.string().required('Please enter your business name'),
    companyNumber: yup.string().required('Please enter your company number')
  });
};

function Step1({ onSubmit }) {
  const unmounted = useUnmounted();
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchEnabledRef = useRef(true);
  const controllerRef = useRef(new AbortController());
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(createValidator()),
    shouldUnregister: true
  });

  const search = useMemo(() => {
    return debounce((searchTerm) => {
      if (searchEnabledRef.current) {
        controllerRef.current.abort();
        controllerRef.current = new AbortController();
        const signal = controllerRef.current.signal;
        setError(null);
        setLoading(true);
        companySearch(searchTerm, signal)
          .then((res) => res.json())
          .then((res) => {
            if (unmounted.current) return;
            setSearchResults(res);
            setLoading(false);
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              setError(
                new Error(
                  "Sorry, we're having trouble searching for your company at the moment. Please try again later or enter your company details manually below"
                )
              );
              setLoading(false);
              reset();
            }
          });
      }
    }, 250);
  }, [reset, unmounted]);

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data, 2))} className={classes.step}>
      <input type="hidden" {...register('businessName')} />
      <input type="hidden" {...register('companyNumber')} />

      {!selectedCompany ? (
        <Fragment>
          <Combobox
            itemToString={(item) => (item ? item.title : '')}
            onInputChange={search}
            onChange={(companyDetails) => {
              setSelectedCompany(companyDetails);
              const { title: businessName, companyNumber } = companyDetails;
              setValue('businessName', businessName, { shouldValidate: true });
              setValue('companyNumber', companyNumber, { shouldValidate: true });
              searchEnabledRef.current = false;
            }}
            options={searchResults}
            label="What is the name of your company?"
            error={error || ((errors.businessName || errors.companyNumber) && new Error('Please select a company'))}
            loading={loading}
          />

          {error ? (
            <Fragment>
              <Input
                id="businessName"
                label="What is your business name?"
                error={errors.businessName}
                {...register('businessName')}
              />

              <Input
                id="companyNumber"
                label="What is your company number?"
                error={errors.companyNumber}
                {...register('companyNumber')}
              />
            </Fragment>
          ) : null}
        </Fragment>
      ) : (
        <div className={classes.company}>
          <div>
            <p>You're applying on behalf of</p>
            <h3>{selectedCompany.title}</h3>
          </div>

          <div className={classes.searchAgain}>
            <p>Not the right company?</p>
            <button
              onClick={() => {
                setSearchResults([]);
                setSelectedCompany(null);
                setValue('businessName', '');
                setValue('companyNumber', '');
                searchEnabledRef.current = true;
              }}
            >
              Search again
            </button>
          </div>

          <dl>
            <dt>Company number</dt>
            <dd>{selectedCompany.companyNumber}</dd>
          </dl>

          <dl>
            <dt>Company status</dt>
            <dd className={classes.companyStatus}>{selectedCompany.companyStatus.toLowerCase()}</dd>
          </dl>

          <dl>
            <dt>Registered office address</dt>
            <dd>{selectedCompany.addressSnippet}</dd>
          </dl>

          <dl>
            <dt>Incorporated on</dt>
            <dd>{selectedCompany.dateOfCreation}</dd>
          </dl>
        </div>
      )}

      <Button variant="secondary">Next</Button>
    </form>
  );
}
Step1.propTypes = {
  onSubmit: PropTypes.func
};

export default Step1;
